import { supabase } from '../lib/supabase';

/**
 * Notification Service
 * Handles all notification-related operations with real-time updates
 */
export const notificationService = {
  /**
   * Fetch all notifications for the current user
   */
  async getNotifications(userId) {
    try {
      const { data, error } = await supabase?.from('notifications')?.select(`
          *,
          related_voucher:vouchers(id, title)
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(notification => ({
        id: notification?.id,
        title: notification?.title,
        message: notification?.message,
        notificationType: notification?.notification_type,
        isRead: notification?.is_read,
        createdAt: notification?.created_at,
        relatedVoucher: notification?.related_voucher ? {
          id: notification?.related_voucher?.id,
          title: notification?.related_voucher?.title
        } : null
      }));
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount(userId) {
    try {
      const { count, error } = await supabase?.from('notifications')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.eq('is_read', false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  },

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    try {
      const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', notificationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(userId) {
    try {
      const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('user_id', userId)?.eq('is_read', false);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking all as read:', error);
      throw error;
    }
  },

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId) {
    try {
      const { error } = await supabase?.from('notifications')?.delete()?.eq('id', notificationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  },

  /**
   * Filter notifications by type
   */
  async filterByType(userId, notificationType) {
    try {
      const { data, error } = await supabase?.from('notifications')?.select(`
          *,
          related_voucher:vouchers(id, title)
        `)?.eq('user_id', userId)?.eq('notification_type', notificationType)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(notification => ({
        id: notification?.id,
        title: notification?.title,
        message: notification?.message,
        notificationType: notification?.notification_type,
        isRead: notification?.is_read,
        createdAt: notification?.created_at,
        relatedVoucher: notification?.related_voucher ? {
          id: notification?.related_voucher?.id,
          title: notification?.related_voucher?.title
        } : null
      }));
    } catch (error) {
      console.error('Error filtering notifications:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time notification updates
   */
  subscribeToNotifications(userId, callback) {
    const channel = supabase?.channel('notifications')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribeFromNotifications(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};